import { RECORD_ENTITY_LABELS, type NoteSource, type RecordEntity } from '@crm/contracts';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState, type Dispatch, type SetStateAction } from 'react';
import { cn } from '../../lib/utils.ts';
import { CheckList } from '../record-table/option-search.tsx';
import { Button } from '../ui/button.tsx';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '../ui/popover.tsx';

/**
 * Which notes a Notes card shows, by the record they are on: a kind absent is not filtered on;
 * with every kind absent, nothing is. A kind present lists its chosen records, or `all`.
 */
export type NoteFilter = Partial<Record<RecordEntity, 'all' | string[]>>;

/** A related record's kind, singular, for the "Job: …" badge on its notes. */
export const NOTE_SOURCE_KIND: Record<RecordEntity, string> = {
  candidates: 'Candidate',
  companies: 'Company',
  contacts: 'Contact',
  projects: 'Project',
  deals: 'Deal',
  jobs: 'Job',
};

const KINDS: RecordEntity[] = ['projects', 'companies', 'contacts', 'deals', 'jobs', 'candidates'];

/** Does a note on this record pass the filter? */
export const passesNoteFilter = (
  filter: NoteFilter,
  entityType: RecordEntity,
  entityId: string,
) => {
  if (Object.keys(filter).length === 0) return true;
  const chosen = filter[entityType];
  return chosen === 'all' || (chosen?.includes(entityId) ?? false);
};

/**
 * The filter without the records that are no longer sources (a contact removed from the project
 * meanwhile); a kind left with none, `all` included, is dropped: its toggle reads off, or is gone
 * and so could not be turned off.
 */
export const pruneNoteFilter = (filter: NoteFilter, sources: readonly NoteSource[]) => {
  const next: NoteFilter = {};
  for (const [kind, chosen] of Object.entries(filter) as [RecordEntity, 'all' | string[]][]) {
    const ids = sources.filter((src) => src.entityType === kind).map((src) => src.entityId);
    const kept = chosen === 'all' ? chosen : chosen.filter((id) => ids.includes(id));
    if (ids.length > 0 && kept.length > 0) next[kind] = kept;
  }
  return next;
};

/**
 * The project Overview's note filter, like the Jobs page's "Mine" / "Open only" toggles: one
 * toggle per kind of record the project has notes from, any number on at once. An active toggle
 * shows under its label what it lets through — "All (n)" or the chosen names (n) — where n
 * counts notes (`counts`, by record id). Over a list (contacts, deals, jobs, candidates) that
 * marker opens the records with their counts, to narrow it to some; clearing the last one turns
 * the toggle off and leaves the list open. The project and its company are one record: no list.
 */
export function NoteOriginFilter({
  sources,
  counts,
  value,
  onChange,
}: {
  sources: readonly NoteSource[];
  counts: ReadonlyMap<string, number>;
  /** Already pruned to the sources (`pruneNoteFilter`). */
  value: NoteFilter;
  onChange: Dispatch<SetStateAction<NoteFilter>>;
}) {
  const [openKind, setOpenKind] = useState<RecordEntity | null>(null);
  const notesOf = (ids: readonly string[]) => ids.reduce((n, id) => n + (counts.get(id) ?? 0), 0);
  const kinds = KINDS.map((kind) => ({
    kind,
    items: sources.filter((src) => src.entityType === kind),
  })).filter((k) => k.items.length > 0);
  const set = (kind: RecordEntity, chosen: 'all' | string[] | undefined) => {
    // From the latest state, so two quick toggles both count.
    onChange((prev) => {
      const next = { ...prev };
      if (chosen === undefined) delete next[kind];
      else next[kind] = chosen;
      return next;
    });
  };

  return (
    <div className="flex flex-wrap items-start gap-1.5" role="group" aria-label="Note origin">
      {kinds.map(({ kind, items }) => {
        const chosen = value[kind];
        // The project and its company are one record each: their marker is a plain count, with
        // nothing to choose among. The other kinds are lists.
        const single = kind === 'projects' || kind === 'companies';
        const shownLabel = single ? NOTE_SOURCE_KIND[kind] : RECORD_ENTITY_LABELS[kind];
        const ids = items.map((i) => i.entityId);
        const picked = chosen === 'all' ? ids : (chosen ?? []);
        const every = picked.length === ids.length;
        const marker = every
          ? `All (${notesOf(ids)})`
          : `${items
              .filter((i) => picked.includes(i.entityId))
              .map((i) => i.label)
              .join(', ')} (${notesOf(picked)})`;
        const toggleItem = (id: string) => {
          const next = picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id];
          set(kind, next.length === 0 ? undefined : next.length === ids.length ? 'all' : next);
        };
        return (
          // The marker line is always there, one fixed height, and as wide as the toggle above it
          // (`w-0 min-w-full`; at least wide enough for "All (n)"): turning toggles on and off
          // never resizes the header.
          <Popover
            key={kind}
            open={openKind === kind}
            onOpenChange={(open) => setOpenKind(open ? kind : null)}
          >
            <PopoverAnchor asChild>
              <div className="flex min-w-16 flex-col items-stretch gap-0.5">
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    'h-6 px-2 text-xs',
                    chosen && 'border-brand bg-brand/10 text-foreground',
                  )}
                  aria-pressed={chosen !== undefined}
                  onClick={() => set(kind, chosen ? undefined : 'all')}
                >
                  {shownLabel}
                </Button>
                {!chosen ? (
                  <span className="h-4" aria-hidden />
                ) : single ? (
                  <span
                    className="text-muted-foreground flex h-4 w-0 min-w-full items-center px-1 text-[11px]"
                    data-testid={`note-origin-${kind}`}
                  >
                    <span className="min-w-0 truncate">{marker}</span>
                  </span>
                ) : (
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground flex h-4 w-0 min-w-full items-center gap-0.5 px-1 text-[11px]"
                      aria-label={`${shownLabel}: ${marker}`}
                      title={marker}
                      data-testid={`note-origin-${kind}`}
                    >
                      <span className="min-w-0 truncate">{marker}</span>
                      <ChevronDown className="size-3 shrink-0" />
                    </button>
                  </PopoverTrigger>
                )}
              </div>
            </PopoverAnchor>
            {!single && (
              <PopoverContent align="start" className="w-64 p-1.5">
                <OriginOptions
                  kind={kind}
                  items={items}
                  counts={counts}
                  total={notesOf(ids)}
                  picked={picked}
                  every={every}
                  onAll={() => set(kind, every ? undefined : 'all')}
                  onToggle={toggleItem}
                />
              </PopoverContent>
            )}
          </Popover>
        );
      })}
    </div>
  );
}

/**
 * "All" with its records under it, as the Stage filter's tree: open each time the list opens
 * (it is mounted with the popover; a project has few of each), the chevron folds them.
 */
function OriginOptions({
  kind,
  items,
  counts,
  total,
  picked,
  every,
  onAll,
  onToggle,
}: {
  kind: RecordEntity;
  items: readonly NoteSource[];
  counts: ReadonlyMap<string, number>;
  /** Notes on all of `items`. */
  total: number;
  picked: readonly string[];
  every: boolean;
  onAll: () => void;
  onToggle: (id: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const name = RECORD_ENTITY_LABELS[kind];
  return (
    <>
      <div className="flex items-center">
        <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-(--v2-dropdown-bg-hover)">
          <input
            type="checkbox"
            aria-label={`All ${name.toLowerCase()}`}
            checked={every}
            ref={(el) => {
              if (el) el.indeterminate = picked.length > 0 && !every;
            }}
            onChange={onAll}
          />
          All
          <span className="text-muted-foreground ml-auto text-xs">{total}</span>
        </label>
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${name.toLowerCase()}`}
          onClick={() => setOpen((o) => !o)}
          className="text-muted-foreground rounded-md p-1 hover:bg-(--v2-dropdown-bg-hover)"
        >
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </button>
      </div>
      {open && (
        <div className="pl-5">
          <CheckList
            label={name}
            items={items.map((i) => ({
              value: i.entityId,
              label: i.label,
              count: counts.get(i.entityId) ?? 0,
            }))}
            selected={[...picked]}
            onToggle={onToggle}
          />
        </div>
      )}
    </>
  );
}
